import { useTranslation } from "react-i18next";
import { AUTO_SETTLE_CHOICES, PERSONAL_MEMORY_POLICIES, WORKFLOW_GRAPH_DENSITIES, WORKFLOW_GRAPH_LAYOUTS } from "../../../api/userPreferencesApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSession } from "../../../session/sessionContext";
import LanguageThemeControls from "../../../ui/controls/LanguageThemeControls";
import OptionCard from "../../../ui/forms/OptionCard";
import { setToolCallDisplay, TOOL_CALL_DISPLAYS, useToolCallDisplay } from "../../threads/preferences/toolCallDisplay";
import SignUpNotifications from "../SignUpNotifications";
import { changePreferences, useUserPreferences } from "./userPreferences";

const HELP_OPENINGS = ["newTab", "samePage"] as const;

/**
 * Personal preferences (F14, option A, PM-8) as short option cards, all alike (F71): language
 * and theme, then the view defaults. Tool calls are kept in this browser; the
 * rest with the account (accounts.md, each user's view settings). The density here is only
 * the default for a Workflow view not yet set (F76).
 */
export default function PersonalPreferences() {
  const { t } = useTranslation();
  const toolCallDisplay = useToolCallDisplay();
  const { preferences, saving, status, error } = useUserPreferences();
  const busy = saving || status === "loading";
  const { session } = useSession();
  const administrator = session.status === "signedIn" && session.session.account.accountRole === "administrator";
  return (
    <>
      <div className="app-option-cards app-option-cards-grid mt-3">
        {/* Its two cards join this grid. */}
        <LanguageThemeControls className="contents" />
        <OptionCard
          label={t("profile.threadView.toolCalls")}
          value={toolCallDisplay}
          options={TOOL_CALL_DISPLAYS.map((display) => ({ value: display, label: t(`profile.threadView.${display}`) }))}
          onChange={setToolCallDisplay}
        />
        <OptionCard
          label={t("profile.threadView.autoSettle")}
          value={preferences.autoSettleAfter}
          options={AUTO_SETTLE_CHOICES.map((choice) => ({ value: choice, label: t(`profile.threadView.autoSettleChoices.${choice}`) }))}
          onChange={(choice) => void changePreferences({ autoSettleAfter: choice })}
          disabled={busy}
        />
        <OptionCard
          label={t("profile.workflowView.density")}
          title={t("profile.workflowView.densityHint")}
          value={preferences.workflowGraphDensity}
          options={WORKFLOW_GRAPH_DENSITIES.map((density) => ({ value: density, label: t(`workflows.density.options.${density}`) }))}
          onChange={(density) => void changePreferences({ workflowGraphDensity: density })}
          disabled={busy}
        />
        <OptionCard
          label={t("profile.workflowView.layout")}
          value={preferences.workflowGraphLayout}
          options={WORKFLOW_GRAPH_LAYOUTS.map((layout) => ({ value: layout, label: t(`workflows.layout.options.${layout}`) }))}
          onChange={(layout) => void changePreferences({ workflowGraphLayout: layout })}
          disabled={busy}
        />
        <OptionCard
          label={t("profile.memories.personalPolicy")}
          value={preferences.personalMemoryPolicy}
          options={PERSONAL_MEMORY_POLICIES.map((policy) => ({ value: policy, label: t(`profile.memories.policies.${policy}`) }))}
          onChange={(policy) => void changePreferences({ personalMemoryPolicy: policy })}
          disabled={busy}
        />
        <OptionCard
          label={t("profile.helpView.opens")}
          value={preferences.openHelpInNewTab ? "newTab" : "samePage"}
          options={HELP_OPENINGS.map((opening) => ({ value: opening, label: t(`profile.helpView.${opening}`) }))}
          onChange={(opening) => void changePreferences({ openHelpInNewTab: opening === "newTab" })}
          disabled={busy}
        />
        {administrator ? <SignUpNotifications /> : null}
      </div>
      {error === null ? null : <ErrorNotice error={error} compact={true} className="mt-3" />}
    </>
  );
}
